import {z} from 'zod';
import {organizationIdSchema} from './id';
import {roleSchema} from './role';

/**
 * Every action that ends up in the audit trail. Keeping the list closed means a
 * reviewer can see the full surface of auditable behaviour in one place, and the
 * frontend can offer it as a filter without inventing strings.
 */
export const AUDIT_ACTIONS = [
  'ORGANIZATION_CREATED',
  'ORGANIZATION_UPDATED',
  'ORGANIZATION_DELETED',
  /**
   * Which GOPACS client an organization dispatches as was set or removed. The client id
   * is in the metadata; the secret is not, here or anywhere else the trail can reach.
   */
  'ORGANIZATION_CALLIE_CREDENTIALS_SET',
  'ORGANIZATION_CALLIE_CREDENTIALS_CLEARED',
  'USER_CREATED',
  'USER_UPDATED',
  'USER_DELETED',
  'USER_PASSWORD_RESET',
  'PROFILE_UPDATED',
  'REQUEST_SUBMITTED',
  'REQUEST_APPROVED',
  'REQUEST_DISAPPROVED',
  'REQUEST_CANCELLED',
  'REQUEST_PUBLISHED',
  /** A person asked for the failed messages of a publication to be sent again. */
  'REQUEST_RETRIED',
  /**
   * System-driven, with a sweep as the actor. `REQUEST_STATUS_AGGREGATED` is the one
   * action whose meaning lives entirely in `fromState`/`toState`: it is written whenever
   * a sweep recomputes a request's status from its messages and the answer changed.
   */
  'REQUEST_EXPIRED',
  'REQUEST_STATUS_AGGREGATED',
  'MESSAGE_CLAIMED',
  'MESSAGE_PUBLISHED',
  'MESSAGE_ACKNOWLEDGED',
  'MESSAGE_FAILED',
  /**
   * Refused by Callie and put back in the queue rather than written off. Distinct from
   * `MESSAGE_FAILED` on purpose: this one says an attempt was spent, and the trail has to
   * show how many a message used before it either went out or gave up (§1).
   */
  'MESSAGE_REQUEUED',
  'MESSAGE_RECONCILED',
  /** Platform-level configuration: which congestion points a tenant may dispatch against. */
  'CONGESTION_AREA_CREATED',
  'CONGESTION_AREA_UPDATED',
  /**
   * Its own action rather than a second `CONGESTION_AREA_UPDATED`: a different person did
   * it, for a different reason. An update is a platform admin changing what an area is; a
   * configuration is the owning organization deciding whether to use it and how hard.
   */
  'CONGESTION_AREA_CONFIGURED',
  'CONGESTION_AREA_DELETED',
  /**
   * The MW ceiling over a cluster of areas changed. Its own action because it is the one
   * setting in this tool that silently makes future dispatches smaller — a reader asking
   * "why was yesterday cut and today was not" needs to find this row.
   */
  'CONGESTION_AREA_CLUSTER_CONFIGURED',
] as const;

export type AuditAction = (typeof AUDIT_ACTIONS)[number];

export const auditActionSchema = z.enum(AUDIT_ACTIONS);

export const AUDIT_OUTCOMES = ['SUCCESS', 'DENIED', 'FAILURE'] as const;

export type AuditOutcome = (typeof AUDIT_OUTCOMES)[number];

export const auditOutcomeSchema = z.enum(AUDIT_OUTCOMES);

export const auditEventSchema = z.object({
  id: z.uuid(),
  occurredAt: z.coerce.date(),
  /** null for platform-level actions such as creating an organization. */
  organizationId: organizationIdSchema.nullable(),
  actorId: z.string(),
  /** Both null when a sweep acted: a machine has no mailbox and no role. */
  actorEmail: z.string().nullable(),
  actorRole: roleSchema.nullable(),
  action: auditActionSchema,
  /** Both ends of a transition; null for an action that is not one. */
  fromState: z.string().nullable().default(null),
  toState: z.string().nullable().default(null),
  outcome: auditOutcomeSchema,
  targetType: z.string().nullable(),
  targetId: z.string().nullable(),
  /** The HTTP request id this action arrived on, for correlation. Null for a sweep. */
  requestId: z.string().nullable(),
  sourceIp: z.string().nullable(),
  metadata: z.record(z.string(), z.unknown()).nullable(),
});

export type AuditEvent = z.infer<typeof auditEventSchema>;

export const auditQuerySchema = z.object({
  organizationId: organizationIdSchema.optional(),
  action: auditActionSchema.optional(),
  from: z.coerce.date().optional(),
  to: z.coerce.date().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  cursor: z.string().optional(),
});

export type AuditQuery = z.infer<typeof auditQuerySchema>;

/**
 * Response contract of `GET /api/audit-events`. Cursor-paged rather than offset-paged:
 * the trail is a DynamoDB query, and a page number has no meaning there.
 */
export const auditPageSchema = z.object({
  events: z.array(auditEventSchema),
  nextCursor: z.string().nullable(),
});

export type AuditPage = z.infer<typeof auditPageSchema>;
